import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { RHFAutoComplete } from '@openx/components-core/lib/AutoComplete/RHFAutoComplete';
import { FormFieldWithInfo } from '@openx/components-core/lib/FormFieldWithInfo/FormFieldWithInfo';
import { IndentedSections } from '@openx/components-core/lib/IndentedSection/IndentedSections';
import { RHFCurrencyField } from '@openx/components-core/lib/NumberField/RHFCurrencyField';
import { RHFRadioField } from '@openx/components-core/lib/RadioField/RHFRadioField';
import { type DealFormData, dealFormField, type PmpDealType, pmpDealType } from '@openx/types/deal';
import {
  BUDGET_MODE,
  BUDGET_TYPE,
  type BudgetMode,
  type BudgetType,
  type MonetizingRegion,
  PACING_MODE,
  type PacingMode,
} from '@openx/types/monetizing';
import { useEffect, useMemo, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { UnionToTuple } from 'type-fest';
import { pacingRegionLabels } from './pacingLabels';

export type PacingFormFieldsProps = {
  dealType?: PmpDealType | null;
  budgetLabel: string;
  canChangeRegion: boolean;
  canChangeGoal: boolean;
  canChangeBudgetMode: boolean;
  regionLabel: string;
  regionOptions: MonetizingRegion[];
  isEdit: boolean;
};

export const PacingFormFields = ({
  budgetLabel,
  canChangeRegion,
  canChangeGoal,
  regionLabel,
  regionOptions,
  canChangeBudgetMode,
  isEdit,
}: PacingFormFieldsProps) => {
  const {
    control,
    setValue,
    trigger,
    clearErrors,
    formState: { defaultValues, isSubmitting, touchedFields, isSubmitted },
  } = useFormContext<DealFormData>();

  const observableDealType = useWatch({ control, name: dealFormField.PMP_DEAL_TYPE });
  const observableMonetizingBudget = useWatch({ control, name: dealFormField.MONETIZING_BUDGET });
  const observableMonetizingMode = useWatch({ control, name: dealFormField.PACING_MODE }) || null;
  const observableBudgetType = useWatch({ control, name: dealFormField.BUDGET_TYPE }) || null;
  const observableBudgetMode = useWatch({ control, name: dealFormField.BUDGET_MODE }) || null;

  const prevBudgetType = useRef<BudgetType | null>(observableBudgetType);
  const monetizingModePrev = useRef<PacingMode | null>(null);
  const budgetModePrev = useRef<BudgetMode | null>(null);
  const isMounted = useRef(false);
  const { t } = useTranslation();

  // Remembers the last value entered per Budget Type while editing, so toggling the
  // Goal radio restores what was there instead of always wiping the field.
  const budgetValueByType = useRef<Partial<Record<BudgetType, string>>>(
    isEdit && defaultValues?.budget_type && defaultValues?.monetizing_budget
      ? { [defaultValues.budget_type]: defaultValues.monetizing_budget }
      : {}
  );
  const latestMonetizingBudget = useRef(observableMonetizingBudget);
  latestMonetizingBudget.current = observableMonetizingBudget;

  const commonExecutingModelText = t(
    'Pacing goals will be met on chosen cadence basis within the deal flight times (Duration) set below.'
  );

  const executingModelInfo =
    observableMonetizingMode === PACING_MODE.EVENLY &&
    (observableBudgetMode === BUDGET_MODE.CAMPAIGN_FLIGHT_TIME || observableDealType === pmpDealType.Guaranteed) ? (
      <span>
        {commonExecutingModelText} {t('With')} <b>{t('Evenly')}</b> {t('option enabled both')} <b>{t('Start')}</b>{' '}
        {t('and')} <b>{t('End Date')}</b> {t('are required.')}
      </span>
    ) : (
      commonExecutingModelText
    );

  useEffect(() => {
    // Trigger end date validation on monetizing mode change
    if (!monetizingModePrev.current) {
      monetizingModePrev.current = observableMonetizingMode;
      return;
    }

    if (monetizingModePrev.current !== observableMonetizingMode) {
      // Only surface the error if the user has already interacted with End Date (or submitted),
      // otherwise this would show a premature "required" error on an untouched field.
      if (observableMonetizingMode === PACING_MODE.ASAP) {
        clearErrors(dealFormField.END_DATE);
      } else if (touchedFields.end_date || isSubmitted) {
        trigger(dealFormField.END_DATE);
      }
      monetizingModePrev.current = observableMonetizingMode;
    }
  }, [observableMonetizingMode, trigger, clearErrors, touchedFields.end_date, isSubmitted]);

  useEffect(() => {
    // Validate immediately on mount when the form already has a preset budget type (e.g. Edit),
    // otherwise only surface the error once the user has touched Budget (or submitted), so
    // switching the Goal radio doesn't show a premature error on an untouched field.
    const isInitialRender = !isMounted.current;
    isMounted.current = true;

    // Clear any stale Budget/Impression Goal error from the previous type before re-validating,
    // so a message for the old type never lingers after switching (e.g. "Impression Goal is
    // required" showing after switching to Budget Spend).
    const isBudgetTypeChanged = !!prevBudgetType.current && prevBudgetType.current !== observableBudgetType;

    if (isBudgetTypeChanged) {
      clearErrors(dealFormField.MONETIZING_BUDGET);
    } else if (prevBudgetType.current && (isInitialRender || touchedFields.monetizing_budget || isSubmitted)) {
      trigger(dealFormField.MONETIZING_BUDGET);
    }

    if (isBudgetTypeChanged && prevBudgetType.current) {
      budgetValueByType.current[prevBudgetType.current] = latestMonetizingBudget.current ?? '';
    }

    prevBudgetType.current = observableBudgetType;

    if (isBudgetTypeChanged) {
      const restoredValue =
        isEdit && observableBudgetType ? (budgetValueByType.current[observableBudgetType] ?? '') : '';
      setValue(dealFormField.MONETIZING_BUDGET, restoredValue);
    }
  }, [trigger, clearErrors, observableBudgetType, touchedFields.monetizing_budget, isSubmitted, isEdit, setValue]);

  useEffect(() => {
    if (budgetModePrev.current !== observableBudgetMode) {
      if (observableBudgetMode === BUDGET_MODE.CADENCE) {
        clearErrors(dealFormField.END_DATE);
      } else if (touchedFields.end_date || isSubmitted) {
        trigger(dealFormField.END_DATE);
      }
    }

    budgetModePrev.current = observableBudgetMode;
  }, [trigger, clearErrors, observableBudgetMode, touchedFields.end_date, isSubmitted]);

  const cadenceOptionsLabels = useMemo(() => {
    return {
      daily: 'Daily (00:00 AM - 11:59 PM)',
      weekly: 'Weekly (Monday - Sunday)',
    } as const;
  }, []);

  const cadenceOptions = useMemo(() => {
    return Object.keys(cadenceOptionsLabels) as UnionToTuple<keyof typeof cadenceOptionsLabels>;
  }, [cadenceOptionsLabels]);

  return (
    <>
      <Typography
        variant="h3"
        data-test="pacing-general-label"
        sx={{
          mt: 2,
          mb: 2,
        }}
      >
        {t('General')}
      </Typography>
      <FormFieldWithInfo
        dataTest="pacing-region"
        info={canChangeRegion && t('Region change is locked if the Start Date is within the next hour.')}
        tooltipTop="18px"
      >
        <RHFAutoComplete
          id={dealFormField.MONETIZING_REGION}
          name={dealFormField.MONETIZING_REGION}
          textFieldProps={{
            label: regionLabel,
            placeholder: t('[ Select Region ]'),
          }}
          disableClearable
          renderOptions={{
            dataTest: 'monetizing-region',
          }}
          getOptionLabel={option => pacingRegionLabels[option] ?? option}
          options={regionOptions}
          disabled={isSubmitting || !canChangeRegion}
          data-test="pacing-region-dropdown"
        />
      </FormFieldWithInfo>
      <Typography
        variant="h3"
        data-test="pacing-goal-label"
        sx={{
          mt: 2,
          mb: 1,
        }}
      >
        {t('Goal')}
      </Typography>
      <FormFieldWithInfo
        dataTest="pacing-goal-type"
        info={canChangeRegion ? null : t('Pacing Goal is locked if the Start Date is within the next hour.')}
        tooltipTop="2px"
      >
        <RHFRadioField
          name={dealFormField.BUDGET_TYPE}
          data-test="pacing-budget-type-group"
          isDisabled={!canChangeGoal}
          options={[
            {
              'data-test': 'radio-spend',
              key: 'radio-spend',
              label: t('Budget Spend'),
              value: BUDGET_TYPE.SPEND,
            },
            {
              'data-test': 'radio-impressions',
              key: 'radio-impressions',
              label: t('Impressions'),
              value: BUDGET_TYPE.IMPRESSIONS,
            },
          ]}
          row
          fullWidth
        />
      </FormFieldWithInfo>
      {observableBudgetType === BUDGET_TYPE.SPEND && (
        <IndentedSections>
          <Alert data-test="pacing-currency-info" severity="info">
            {t('Pacing Currency is in USD.')}
          </Alert>
        </IndentedSections>
      )}
      <Typography
        variant="h3"
        data-test="pacing-budgeting-label"
        sx={{
          mt: 2,
          mb: 1,
        }}
      >
        {t('Budgeting Mode')}
      </Typography>
      <RHFRadioField
        name={dealFormField.BUDGET_MODE}
        isDisabled={!canChangeBudgetMode}
        data-test="pacing-budget-mode-group"
        options={[
          {
            'data-test': 'radio-campaign',
            key: 'radio-campaign',
            label: t('Campaign Flight Time'),
            value: BUDGET_MODE.CAMPAIGN_FLIGHT_TIME,
          },
          {
            'data-test': 'radio-cadence',
            key: 'radio-cadence',
            label: t('Cadence'),
            value: BUDGET_MODE.CADENCE,
          },
        ]}
        row
        fullWidth
      />
      <IndentedSections>
        <Typography variant="overline" data-test="pacing-budgeting-option-label">
          {observableBudgetMode === BUDGET_MODE.CADENCE ? t('CADENCE') : t('CAMPAIGN FLIGHT TIME')}
        </Typography>

        <Grid container spacing={1}>
          <Grid size={observableBudgetMode === BUDGET_MODE.CAMPAIGN_FLIGHT_TIME ? 12 : 4}>
            <RHFCurrencyField
              currency={observableBudgetType === BUDGET_TYPE.SPEND && 'USD'}
              label={
                (observableBudgetMode === BUDGET_MODE.CAMPAIGN_FLIGHT_TIME ? `${t('Campaign')} ` : '') + budgetLabel
              }
              dataTest={observableBudgetType === BUDGET_TYPE.SPEND ? 'pacing-budget' : 'pacing-impressions'}
              name={dealFormField.MONETIZING_BUDGET}
              placeholder={
                observableBudgetType === BUDGET_TYPE.SPEND ? '0.00' : t('Enter an Impression Goal e.g. 1000')
              }
              onClearClick={
                !!observableMonetizingBudget &&
                (() => {
                  setValue(dealFormField.MONETIZING_BUDGET, '', { shouldDirty: true, shouldValidate: true });
                })
              }
            />
          </Grid>
          {observableBudgetMode === BUDGET_MODE.CADENCE && (
            <Grid size={8}>
              <FormFieldWithInfo
                autoAlign
                dataTest="pacing-cadence"
                info={
                  <>
                    {t(
                      'Spending starts at midnight local time. Depending on the region your deal runs in, the time will be adjusted to the following time zones:'
                    )}
                    <br />
                    EMEA: UTC+1:00
                    <br />
                    NA & LATAM: UTC-6:00
                    <br />
                    APAC: UTC+8:00
                  </>
                }
                tooltipTop="26px"
              >
                <RHFAutoComplete
                  name={dealFormField.MONETIZING_CADENCE_UNIT}
                  textFieldProps={{
                    label: t('Cadence Type'),
                    placeholder: t('Select Cadence Type'),
                  }}
                  getOptionLabel={option => cadenceOptionsLabels[option]}
                  options={cadenceOptions}
                  disableClearable
                  fullWidth
                  data-test={'pacing-cadence-type'}
                />
              </FormFieldWithInfo>
            </Grid>
          )}
        </Grid>
      </IndentedSections>
      <Typography
        variant="h3"
        data-test="executing-model-label"
        sx={{
          mt: 2,
          mb: 1,
        }}
      >
        {t('Executing Model')}
      </Typography>
      <RHFRadioField
        name={dealFormField.PACING_MODE}
        data-test="pacing-radio-group"
        options={[
          {
            'data-test': 'radio-asap',
            key: 'radio-asap',
            label: t('ASAP'),
            value: PACING_MODE.ASAP,
          },
          {
            'data-test': 'radio-evenly',
            key: 'radio-evenly',
            label: t('Evenly'),
            value: PACING_MODE.EVENLY,
          },
        ]}
        row
        fullWidth
      />
      <IndentedSections>
        <Box
          sx={{
            mb: 1,
            mt: 1,
          }}
        >
          <Alert data-test="publisher-info" severity="info">
            {executingModelInfo}
          </Alert>
        </Box>
      </IndentedSections>
    </>
  );
};
